// The places the Map shows (docs/PLACES.md): one entry per place, in Explore-all order. Each place
// is its own lazily loaded chunk (`load`), so the app's first load doesn't grow with them.
// Three-free: the switcher (main bundle) reads the titles.
//
// No place is special: the active trip's places are the ones its stages stand on (`stage.spot`,
// tripPlaces below). Those follow the trip's live weather, come first in the island switcher and
// are remembered as the last island shown (stage/bridge, stage/mapIslands); any place can be one.
import type { PlaceDef } from '../placekit/types'

export interface PlaceEntry {
  id: string
  title: string
  jp: string
  /** The country whose pack unlocks it (C1, shared/catalogue.ts; ISO 3166-1, lower case). */
  country: 'jp' | 'kr'
  /**
   * The language the place's sound words are in and spoken in: 'ja' (the default; Zundamon via
   * VOICEVOX) or 'ko' (Hangul beats, the Korean MeloTTS voice). The voice pipeline reads it
   * (scripts/places-sound/voices.mjs); the font stack covers both.
   */
  lang?: 'ja' | 'ko'
  load(): Promise<{ default: PlaceDef }>
}

export const PLACES: PlaceEntry[] = [
  { id: 'kyoto', title: 'Kyoto', jp: '京都', country: 'jp', load: () => import('./kyoto/place') },
  { id: 'tokyo', title: 'Tokyo', jp: '東京', country: 'jp', load: () => import('./tokyo/place') },
  { id: 'osaka', title: 'Osaka', jp: '大阪', country: 'jp', load: () => import('./osaka/place') },
  { id: 'nara', title: 'Nara', jp: '奈良', country: 'jp', load: () => import('./nara/place') },
  { id: 'castletown', title: 'Castle Town', jp: '城下町', country: 'jp', load: () => import('./castletown/place') },
  { id: 'shirakawago', title: 'Shirakawa-go', jp: '白川郷', country: 'jp', load: () => import('./shirakawago/place') },
  { id: 'miyajima', title: 'Miyajima', jp: '宮島', country: 'jp', load: () => import('./miyajima/place') },
  { id: 'fuji', title: 'Fuji · Hakone', jp: '富士', country: 'jp', load: () => import('./fuji/place') },
  { id: 'kamakura', title: 'Kamakura', jp: '鎌倉', country: 'jp', load: () => import('./kamakura/place') },
  { id: 'bigcity', title: 'Big City', jp: '大都市', country: 'jp', load: () => import('./bigcity/place') },
  { id: 'fukuoka', title: 'Fukuoka', jp: '福岡', country: 'jp', load: () => import('./fukuoka/place') },
  { id: 'okinawa', title: 'Okinawa', jp: '沖縄', country: 'jp', load: () => import('./okinawa/place') },
  { id: 'village', title: 'Mountain Village', jp: '山村', country: 'jp', load: () => import('./village/place') },
  { id: 'seoul', title: 'Seoul', jp: '서울', country: 'kr', lang: 'ko', load: () => import('./seoul/place') },
  { id: 'hangang', title: 'Han River', jp: '한강', country: 'kr', lang: 'ko', load: () => import('./hangang/place') },
  { id: 'sapporo', title: 'Sapporo', jp: '札幌', country: 'jp', load: () => import('./sapporo/place') },
  { id: 'otaru', title: 'Otaru', jp: '小樽', country: 'jp', load: () => import('./otaru/place') },
  { id: 'niseko', title: 'Niseko', jp: 'ニセコ', country: 'jp', load: () => import('./niseko/place') },
]

/**
 * Old place ids → today's (Niseko was `mountain` until 2026-10). Stored keys (the remembered
 * island), cached packs, synced trip edits and the live database's stage spots may still name a
 * place by an old id; read every place id through placeId(). Old asset paths are mapped by
 * worker/spa.ts (the server) and src/sw.ts (sounds cached under the old folder).
 */
export const PLACE_ALIASES: Readonly<Record<string, string>> = { mountain: 'niseko' }

/** Today's id for a place id that may be an old one (anything else comes back as it was). */
export const placeId = (id: string): string => PLACE_ALIASES[id] ?? id

/**
 * Today's id for a place's sound cue that may be an old one: cues are `<place>_<name>`, so an
 * old place id's prefix becomes today's (`mountain_pyon` → `niseko_pyon`).
 */
export function cueId(cue: string): string {
  const i = cue.indexOf('_')
  const to = i > 0 ? PLACE_ALIASES[cue.slice(0, i)] : undefined
  return to ? to + cue.slice(i) : cue
}

/** The place's folder under src/map/places, art/places, public/places and public/voices: its id. */
export const assetDir = (id: string): string => placeId(id)

/** The trip's places: the places its stages stand on (`spot`), in stage order, each once. */
export function tripPlaces(stages: readonly { spot: string }[]): string[] {
  const out: string[] = []
  for (const s of stages) {
    const id = placeId(s.spot)
    if (!out.includes(id) && PLACES.some((p) => p.id === id)) out.push(id)
  }
  return out
}

/** Every place in the island switcher's order: the trip's places first (stage order), then the rest. */
export function placeOrder(trip: readonly string[]): PlaceEntry[] {
  const first = trip.flatMap((id) => PLACES.filter((p) => p.id === id))
  return [...first, ...PLACES.filter((p) => !trip.includes(p.id))]
}
